/**
 * Vehicle service: replacement flow + reading the current vehicle. Heavier
 * writes (atomic index updates) belong in a Cloud Function — see
 * `transferOwnershipToCurrentUser` / `replaceUserPlate` already in `functions/`.
 * This service handles the client-side orchestration for those calls.
 *
 * Replaces `my-car-render.js::replaceVehicle` (legacy did the write inline
 * with non-atomic index updates).
 */

import { auth, db, ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Auth } from '../../app/config.js';
import { normalize as normalizePlate, isValid as isValidPlate } from '../../shared/plate.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import * as bus from '../../core/eventBus.js';
import { createLogger } from '../../core/logger.js';

import { getDeviceId, authorizeForPlate } from '../auth/device.service.js';
import * as deviceWatcher from '../auth/deviceWatcher.js';
import { getLastOwnership, persistOwnership, matchOwnershipMonth, matchProductionYear } from './ownership.service.js';

const log = createLogger('vehicle');

/**
 * @typedef {Object} ReplaceInput
 * @property {string} newPlate
 * @property {string} year   4-digit production year (shnat_yitzur)
 */

/**
 * @typedef {Object} ReplaceResult
 * @property {'ok' | 'mismatch' | 'taken' | 'error'} status
 * @property {string} [message]
 * @property {string} [govYear]   Gov-reported production year on mismatch
 */

/**
 * Replace the current user's plate. Verifies ownership month against the
 * gov registry, then performs an ATOMIC swap.
 *
 * Architecture — three strict phases. Anything failing in phase 1 returns
 * before a single byte is written. Phase 2 is a single multi-path update
 * (all-or-nothing). Phase 3 is best-effort post-ops that can't leave the
 * core state half-written even if they fail.
 *
 *   Phase 1 — VALIDATE     (read-only checks; no writes)
 *   Phase 2 — ATOMIC BATCH (single ref().update() with all paths)
 *   Phase 3 — POST-OPS     (conversation cleanup, ownership history, device
 *                           authorize — failures here are warned but the
 *                           replace is still considered successful)
 *
 * This replaces the legacy multi-step approach where a failure mid-flow
 * (e.g. `safeRemove` denied by a rule) silently left half-written state —
 * the new plate registered AND the old plate still registered, producing
 * the "two cars under one user" inconsistency in Firebase.
 *
 * @param {ReplaceInput} input
 * @returns {Promise<ReplaceResult>}
 */
export async function replace({ newPlate, year }) {
  // ===================================================================
  // PHASE 1 — VALIDATE
  // ===================================================================
  const plate = normalizePlate(newPlate);
  if (!isValidPlate(plate)) return { status: 'error', message: 'מספר רכב לא תקין.' };

  const yearStr = String(year || '').replace(/\D/g, '');
  if (!/^\d{4}$/.test(yearStr)) {
    return { status: 'error', message: 'יש למלא שנת ייצור (4 ספרות).' };
  }

  const uid = auth().currentUser?.uid;
  if (!uid) return { status: 'error', message: 'יש להתחבר לפני החלפת רכב.' };

  const phone = normalizePhone(LocalState.phone ?? '');
  if (!phone) return { status: 'error', message: 'מספר טלפון של החשבון לא נמצא.' };

  const oldPlate = normalizePlate(LocalState.plate ?? '');
  if (oldPlate && oldPlate === plate) {
    return { status: 'error', message: 'זה כבר הרכב הנוכחי שלך.' };
  }

  // 1a. Production-year check against gov registry (shnat_yitzur).
  // Replaces the old ownership-month check — `shnat_yitzur` exists on every
  // vehicle, whereas `baalut_dt` only covered post-2017 ownership changes.
  // If gov is unreachable (`down`), proceed with a warning (motIsDown).
  const yearCheck = await matchProductionYear(plate, yearStr);
  if (!yearCheck.matches && !yearCheck.down) {
    return {
      status: 'mismatch',
      govYear: yearCheck.govYear ?? undefined,
      // SECURITY: never include `yearCheck.govYear` in the user-facing
      // message — that would defeat the whole verification (the user could
      // just re-submit the year we just told them is correct). Generic
      // "doesn't match" only.
      message: yearCheck.govYear
        ? `שנת הייצור שהוזנה (${yearStr}) אינה תואמת לרישומי משרד התחבורה.`
        : 'לא נמצא רישום של רכב זה במאגר משרד התחבורה.',
    };
  }

  // 1b. plateIndex pre-check — is someone else holding this plate?
  /** @type {string | null} */
  let plateOwner = null;
  try {
    plateOwner = /** @type {any} */ ((await ref(`plateIndex/${plate}`).once('value')).val());
  } catch (err) {
    log.warn('plateIndex pre-check denied:', /** @type {Error} */ (err).message);
  }
  if (plateOwner && plateOwner !== uid) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר.' };
  }

  // 1c. vehicles record pre-check — old ownerId blocking the rule.
  /** @type {any} */
  let existingVehicleOwnerId = null;
  try {
    const snap = await ref(`vehicles/${plate}/ownerId`).once('value');
    existingVehicleOwnerId = snap.val();
  } catch (err) {
    log.warn('vehicles pre-check denied:', /** @type {Error} */ (err).message);
  }
  if (existingVehicleOwnerId && existingVehicleOwnerId !== uid) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר (vehicles).' };
  }

  // 1d. Ownership history record (read; not written yet) — best-effort cache
  // population. Not used as a precondition any more (production year is the
  // verification factor now), so a missing record is harmless here.
  const lastOwnership = await getLastOwnership(plate);
  // Production year is now always validated above — pass it through.
  const finalYear = yearStr;
  const now = Date.now();

  // ===================================================================
  // PHASE 2 — ATOMIC BATCH WRITE
  // All-or-nothing. If even one path is denied by rules, the entire
  // batch is rejected and the database stays in its pre-replace state.
  // ===================================================================

  // Stop the device watcher BEFORE the batch fires. The batch wipes
  // `vehicles/{oldPlate}/devices` which the presence watcher would otherwise
  // interpret as "this device was revoked" and trigger a hardLogout — kicking
  // the user out of the app mid-swap and forcing a full OTP re-login.
  // The watcher is restarted with the new plate in Phase 3.
  try { deviceWatcher.stop(); }
  catch (err) { log.warn('deviceWatcher.stop before swap failed:', /** @type {Error} */ (err).message); }

  // 2a. Race-safe plateIndex claim (separate transaction since multi-path
  //     update doesn't support per-path conditional logic).
  let claim;
  try {
    claim = await ref(`plateIndex/${plate}`).transaction(
      (/** @type {any} */ cur) => (cur === null || cur === uid ? uid : undefined),
    );
  } catch (err) {
    log.error('plateIndex transaction failed:', err);
    return { status: 'error', message: 'נדחתה כתיבה לאינדקס הרכב. נסה שוב.' };
  }
  if (!claim.committed) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר.' };
  }

  // 2b. Build the multi-path update. Every write — new state AND old-plate
  //     cleanup — is included so the rules-check is a single decision.
  /** @type {Record<string, any>} */
  const updates = {
    // New vehicle record
    [`vehicles/${plate}/ownerId`]: uid,
    [`vehicles/${plate}/phone`]: phone,
    [`vehicles/${plate}/plate`]: plate,
    [`vehicles/${plate}/year`]: finalYear,
    [`vehicles/${plate}/lastOwnership`]: lastOwnership.dateISO ?? null,
    [`vehicles/${plate}/lastOwnershipUpdatedAt`]: now,
    [`vehicles/${plate}/updatedAt`]: now,
    // Clear any leftover "mismatch" flag from a previous owner of this
    // plate — we just gov-verified the user's ownership, so the banner
    // for the new plate must start clean.
    [`vehicles/${plate}/ownershipMismatch`]: null,
    // NOTE: stale-device cleanup on the NEW plate is NOT in this atomic
    // batch — see register()'s identical NOTE. Phase 3 does it best-effort
    // via per-deviceId removes which use the permissive child rule.
    // The OLD plate's devices ARE wiped here (different rule path — see below).
    // User record (top-level summary fields)
    [`users/${uid}/plate`]: plate,
    // Mirror the phone back onto the user record on every replace. The phone
    // doesn't change here, but rewriting it self-heals legacy / cleanup
    // accidents where `users/{uid}/phone` was lost — without it the call
    // Cloud Function falls back to vehicles/{plate}/phone every time.
    [`users/${uid}/phone`]: phone,
    [`users/${uid}/year`]: finalYear,
    [`users/${uid}/lastOwnership`]: lastOwnership.dateISO ?? null,
    [`users/${uid}/updatedAt`]: now,
    // Per-user / per-phone linkage
    [`users/${uid}/plates/${plate}/linkedAt`]: now,
    [`phones/${phone}/plates/${plate}`]: true,
  };

  if (oldPlate && oldPlate !== plate) {
    // Detach the OLD plate from this user — fully released so it can be
    // claimed by its new physical owner without rule conflicts.
    Object.assign(updates, {
      [`plateIndex/${oldPlate}`]: null,
      [`users/${uid}/plates/${oldPlate}`]: null,
      [`phones/${phone}/plates/${oldPlate}`]: null,
      // Strip ownership fields from the vehicle record but keep the `gov`
      // sub-tree so the next owner — and any user who searches this plate —
      // benefits from the cached registry data.
      [`vehicles/${oldPlate}/ownerId`]: null,
      [`vehicles/${oldPlate}/phone`]: null,
      [`vehicles/${oldPlate}/year`]: null,
      [`vehicles/${oldPlate}/lastOwnership`]: null,
      [`vehicles/${oldPlate}/updatedAt`]: now,
      // Wipe ALL authorised devices on the released plate. Without this
      // the previous owner's deviceId stays in
      // `vehicles/{oldPlate}/devices/{deviceId}` and watchers / push
      // routing might still treat that device as "valid" on a plate it no
      // longer owns. The next owner will re-authorise their own device
      // on first login via `authorizeForPlate`.
      [`vehicles/${oldPlate}/devices`]: null,
    });
  }

  // 2c. Fire the atomic batch.
  try {
    await db().ref().update(updates);
  } catch (err) {
    const message = /** @type {Error} */ (err).message || '';
    log.error('atomic replace batch failed:', message);

    // Roll back the plateIndex claim from step 2a — without this, the
    // user can't retry because the index is "owned by me" but nothing
    // else points to the new plate.
    try {
      await ref(`plateIndex/${plate}`).transaction(
        (/** @type {any} */ cur) => (cur === uid ? null : cur),
      );
    } catch (rb) {
      log.warn('plateIndex rollback failed:', /** @type {Error} */ (rb).message);
    }

    // Translate common Firebase errors to user-friendly Hebrew.
    if (/permission_denied/i.test(message) || /PERMISSION_DENIED/.test(message)) {
      return {
        status: 'error',
        message: 'הכתיבה ל-DB נדחתה. ייתכן שהרכב משויך כבר למישהו אחר או שהחשבון שלך נחסם. פנה לתמיכה.',
      };
    }
    return { status: 'error', message: 'שגיאה בהחלפת הרכב — לא בוצע שינוי. נסה שוב.' };
  }

  // ===================================================================
  // PHASE 3 — POST-OPS (best-effort; failures don't roll back)
  // The DB is now in a consistent post-replace state. The post-ops below
  // are non-critical — if any of them fails the user is still on the new
  // plate, the old plate is still released. We log the failure but report
  // the replace as successful.
  // ===================================================================
  LocalState.plate = plate;
  if (year) LocalState.carYear = year;

  if (oldPlate && oldPlate !== plate) {
    try { await detachOldConversations(uid, oldPlate); }
    catch (err) { log.warn('detachOldConversations failed:', /** @type {Error} */ (err).message); }
  }

  try { await persistOwnership(plate, phone, lastOwnership); }
  catch (err) { log.warn('persistOwnership failed:', /** @type {Error} */ (err).message); }

  try { await authorizeForPlate(plate); }
  catch (err) { log.warn('authorizeForPlate failed:', /** @type {Error} */ (err).message); }

  // Successful replace ⇒ wipe the failed-attempts counter. Without this,
  // a user who got the year wrong once and then succeeded would still
  // start tomorrow's attempts at count=1, which surprises them and risks
  // a false lockout. Mirrors the same cleanup that runs after a successful
  // login in auth.service.js.
  try { await ref(`attempts/${phone}`).remove(); }
  catch (err) { log.warn('attempts cleanup after replace failed:', /** @type {Error} */ (err).message); }

  // Stale-device cleanup on the newly-claimed plate — see register() for the
  // rationale. Per-deviceId removes use the permissive child rule.
  try { await wipeStaleDevices(plate); }
  catch (err) { log.warn('wipeStaleDevices after replace failed:', /** @type {Error} */ (err).message); }

  // Re-arm the watcher on the NEW plate. Without this, the user is still
  // signed in but `vehicles/{newPlate}/devices/{deviceId}` revocations would
  // go undetected after the swap.
  try { deviceWatcher.start({ uid, plate }); }
  catch (err) { log.warn('deviceWatcher.start after swap failed:', /** @type {Error} */ (err).message); }

  // Best-effort verification: read plateIndex/{oldPlate} back and warn loudly
  // if it's NOT null. With the atomic batch above this should never trigger,
  // but logging it surfaces unexpected rule states early.
  if (oldPlate && oldPlate !== plate) {
    try {
      const check = await ref(`plateIndex/${oldPlate}`).once('value');
      if (check.val() != null) {
        log.error('CLEANUP CHECK: plateIndex/' + oldPlate + ' STILL set to', check.val());
      }
    } catch { /* ignore */ }
  }

  bus.emit('vehicle:changed', { plate });
  return { status: 'ok' };
}

/**
 * @typedef {Object} RegisterInput
 * @property {string} plate
 * @property {string} year   4-digit production year (shnat_yitzur)
 */

/**
 * @typedef {Object} RegisterResult
 * @property {'ok' | 'mismatch' | 'taken' | 'error'} status
 * @property {string} [message]
 * @property {string} [govYear]   Gov-reported production year (shnat_yitzur) on mismatch
 */

/**
 * First-time vehicle registration — called by the new SMS-only login flow's
 * "register a vehicle" modal AFTER the user is already signed in by phone.
 *
 * Conceptually a `replace()` without an old plate to release. We share the
 * same Phase 1 (validate) / Phase 2 (atomic batch) / Phase 3 (post-ops)
 * structure so all the rules-edge-case work pays off here too.
 *
 * Verification is via **production year (shnat_yitzur)** — the legacy 2017
 * ownership-history dataset miss-rate was too high (cars whose last sale
 * predated 2017 had no record). The main vehicles dataset has `shnat_yitzur`
 * on every record, so this check works for every plate.
 *
 * @param {RegisterInput} input
 * @returns {Promise<RegisterResult>}
 */
export async function register({ plate: rawPlate, year }) {
  // ===================================================================
  // PHASE 1 — VALIDATE
  // ===================================================================
  const plate = normalizePlate(rawPlate);
  if (!isValidPlate(plate)) return { status: 'error', message: 'מספר רכב לא תקין.' };

  const yearStr = String(year || '').replace(/\D/g, '');
  if (!/^\d{4}$/.test(yearStr)) {
    return { status: 'error', message: 'יש למלא שנת ייצור (4 ספרות).' };
  }

  const uid = auth().currentUser?.uid;
  if (!uid) return { status: 'error', message: 'יש להתחבר לפני רישום רכב.' };

  const phone = normalizePhone(LocalState.phone ?? '');
  if (!phone) return { status: 'error', message: 'מספר טלפון של החשבון לא נמצא.' };

  const oldPlate = normalizePlate(LocalState.plate ?? '');
  if (oldPlate && oldPlate !== plate) {
    // Already has a vehicle — should use replace flow instead.
    return { status: 'error', message: 'כבר רשום רכב לחשבון. להחלפת רכב — האזור האישי → הרכב שלי → החלף רכב רשום.' };
  }

  // 1a. Production-year check against gov registry (shnat_yitzur).
  // If the gov registry is unreachable (`down: true`) we proceed with a
  // warning rather than blocking — matches the 3-step login's `motIsDown`
  // semantics. The view layer surfaces the warning to the user.
  const yearCheck = await matchProductionYear(plate, yearStr);
  if (!yearCheck.matches && !yearCheck.down) {
    return {
      status: 'mismatch',
      govYear: yearCheck.govYear ?? undefined,
      // SECURITY: never include `yearCheck.govYear` in the user-facing
      // message — that would defeat the whole verification (the user could
      // just re-submit the year we just told them is correct). Generic
      // "doesn't match" only.
      message: yearCheck.govYear
        ? `שנת הייצור שהוזנה (${yearStr}) אינה תואמת לרישומי משרד התחבורה.`
        : 'לא נמצא רישום של רכב זה במאגר משרד התחבורה.',
    };
  }

  // 1b. plateIndex pre-check.
  /** @type {string | null} */
  let plateOwner = null;
  try {
    plateOwner = /** @type {any} */ ((await ref(`plateIndex/${plate}`).once('value')).val());
  } catch (err) {
    log.warn('plateIndex pre-check denied:', /** @type {Error} */ (err).message);
  }
  if (plateOwner && plateOwner !== uid) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר.' };
  }

  // 1c. vehicles record pre-check.
  /** @type {any} */
  let existingVehicleOwnerId = null;
  try {
    const snap = await ref(`vehicles/${plate}/ownerId`).once('value');
    existingVehicleOwnerId = snap.val();
  } catch (err) {
    log.warn('vehicles pre-check denied:', /** @type {Error} */ (err).message);
  }
  if (existingVehicleOwnerId && existingVehicleOwnerId !== uid) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר.' };
  }

  // 1d. Ownership history.
  const lastOwnership = await getLastOwnership(plate);
  const now = Date.now();

  // ===================================================================
  // PHASE 2 — ATOMIC BATCH WRITE
  // ===================================================================

  // Stop the device watcher before the batch (we'll restart in Phase 3
  // pointing at the just-claimed plate).
  try { deviceWatcher.stop(); }
  catch (err) { log.warn('deviceWatcher.stop before register failed:', /** @type {Error} */ (err).message); }

  let claim;
  try {
    claim = await ref(`plateIndex/${plate}`).transaction(
      (/** @type {any} */ cur) => (cur === null || cur === uid ? uid : undefined),
    );
  } catch (err) {
    log.error('plateIndex transaction failed:', err);
    return { status: 'error', message: 'נדחתה כתיבה לאינדקס הרכב. נסה שוב.' };
  }
  if (!claim.committed) {
    return { status: 'taken', message: 'הרכב משויך כבר למשתמש אחר.' };
  }

  /** @type {Record<string, any>} */
  const updates = {
    [`vehicles/${plate}/ownerId`]: uid,
    [`vehicles/${plate}/phone`]: phone,
    [`vehicles/${plate}/plate`]: plate,
    [`vehicles/${plate}/year`]: yearStr,
    [`vehicles/${plate}/lastOwnership`]: lastOwnership.dateISO ?? null,
    [`vehicles/${plate}/lastOwnershipUpdatedAt`]: now,
    [`vehicles/${plate}/updatedAt`]: now,
    [`vehicles/${plate}/ownershipMismatch`]: null,
    // NOTE: stale-device cleanup (vehicles/{plate}/devices wipe) is INTENTIONALLY
    // NOT in this atomic batch. Writing `devices: null` failed under the
    // vehicles/$plate rule when the plate previously had a non-self owner —
    // Firebase evaluates each multi-path write independently, so `newData`
    // for that path doesn't reflect our sibling `ownerId: uid` write.
    // Phase 3 does the cleanup as a best-effort follow-up, one deviceId at
    // a time (the per-deviceId rule is `auth != null`, always permissive).
    [`users/${uid}/plate`]: plate,
    [`users/${uid}/phone`]: phone,
    [`users/${uid}/year`]: yearStr,
    [`users/${uid}/lastOwnership`]: lastOwnership.dateISO ?? null,
    [`users/${uid}/updatedAt`]: now,
    [`users/${uid}/plates/${plate}/linkedAt`]: now,
    [`phones/${phone}/plates/${plate}`]: true,
  };

  try {
    await db().ref().update(updates);
  } catch (err) {
    const message = /** @type {Error} */ (err).message || '';
    log.error('atomic register batch failed:', message);
    // Roll back the plateIndex claim.
    try {
      await ref(`plateIndex/${plate}`).transaction(
        (/** @type {any} */ cur) => (cur === uid ? null : cur),
      );
    } catch (rb) { log.warn('plateIndex rollback failed:', /** @type {Error} */ (rb).message); }
    if (/permission_denied/i.test(message)) {
      return { status: 'error', message: 'הכתיבה ל-DB נדחתה. ייתכן שהרכב משויך כבר למישהו אחר. פנה לתמיכה.' };
    }
    return { status: 'error', message: 'שגיאה ברישום הרכב — לא בוצע שינוי. נסה שוב.' };
  }

  // ===================================================================
  // PHASE 3 — POST-OPS (best-effort)
  // ===================================================================
  LocalState.plate = plate;
  LocalState.carYear = yearStr;

  try { await persistOwnership(plate, phone, lastOwnership); }
  catch (err) { log.warn('persistOwnership failed:', /** @type {Error} */ (err).message); }

  try { await authorizeForPlate(plate); }
  catch (err) { log.warn('authorizeForPlate failed:', /** @type {Error} */ (err).message); }

  // Successful register ⇒ wipe the failed-attempts counter. See replace()
  // above for the rationale (avoid carrying a stale 1-2 attempt count
  // into the next day, which would risk a surprise lockout).
  try { await ref(`attempts/${phone}`).remove(); }
  catch (err) { log.warn('attempts cleanup after register failed:', /** @type {Error} */ (err).message); }

  // Stale-device cleanup — read existing device entries on this plate and
  // remove any that aren't ours. Per-deviceId writes use the permissive
  // `vehicles/$plate/devices/$deviceId/.write: "auth != null"` rule and
  // don't trigger the parent owner-match check. Best-effort: a failure
  // here doesn't roll back the registration.
  try { await wipeStaleDevices(plate); }
  catch (err) { log.warn('wipeStaleDevices after register failed:', /** @type {Error} */ (err).message); }

  try { deviceWatcher.start({ uid, plate }); }
  catch (err) { log.warn('deviceWatcher.start after register failed:', /** @type {Error} */ (err).message); }

  bus.emit('vehicle:changed', { plate });
  return { status: 'ok' };
}

/**
 * Remove device entries on `vehicles/{plate}/devices` that don't belong to
 * THIS device. Used after register/replace claims a plate that may carry
 * leftover device records from a previous owner. Each delete is a separate
 * write, but the rule on `vehicles/$plate/devices/$deviceId` is `auth != null`
 * so they all succeed regardless of who the previous owner was.
 *
 * @param {string} plate
 */
async function wipeStaleDevices(plate) {
  const myDeviceId = getDeviceId();
  /** @type {any} */
  let devicesSnap;
  try {
    devicesSnap = await ref(`vehicles/${plate}/devices`).once('value');
  } catch (err) {
    log.warn('wipeStaleDevices: read failed:', /** @type {Error} */ (err).message);
    return;
  }
  if (!devicesSnap.exists()) return;
  /** @type {Promise<void>[]} */
  const removals = [];
  devicesSnap.forEach((/** @type {any} */ child) => {
    const id = child.key;
    if (id && id !== myDeviceId) {
      removals.push(
        ref(`vehicles/${plate}/devices/${id}`).remove().catch((/** @type {Error} */ e) => {
          log.warn(`wipeStaleDevices: remove ${id} failed:`, e.message);
        }),
      );
    }
  });
  await Promise.all(removals);
  log.info(`wipeStaleDevices: cleaned ${removals.length} stale entries on ${plate}`);
}

/**
 * @returns {Promise<{ plate: string | null; year: string | null }>}
 */
export async function getCurrent() {
  const uid = auth().currentUser?.uid;
  if (!uid) return { plate: null, year: null };
  /** @type {any} */
  const u = (await ref(`users/${uid}`).once('value')).val() ?? {};
  return { plate: normalizePlate(u.plate ?? ''), year: u.year ?? null };
}

/**
 * Remove every userConversations entry for `uid` whose conversation involves
 * `oldPlate` — used after a plate replacement so the user's inbox doesn't
 * keep showing threads from the previous vehicle.
 *
 * Detection: convId is `[plateA, plateB].sort().join('_')`. Each entry stores
 * `peerPlate`, so we know the OTHER side; the user's plate at the time of save
 * was oldPlate. If `conversationKey(oldPlate, peerPlate) === convId`, the entry
 * belongs to the old vehicle and gets dropped.
 *
 * @param {string} uid
 * @param {string} oldPlate
 */
async function detachOldConversations(uid, oldPlate) {
  if (!uid || !oldPlate) return;
  try {
    const snap = await ref(`userConversations/${uid}`).once('value');
    const val = snap.val();
    if (!val || typeof val !== 'object') return;
    /** @type {Record<string, null>} */
    const updates = {};
    for (const [convId, entry] of Object.entries(/** @type {any} */ (val))) {
      const peerPlate = String(/** @type {any} */ (entry)?.peerPlate ?? '');
      if (!peerPlate) continue;
      const expected = [oldPlate, peerPlate].sort().join('_');
      if (convId === expected) {
        updates[`userConversations/${uid}/${convId}`] = null;
      }
    }
    if (Object.keys(updates).length > 0) {
      await db().ref().update(updates);
      log.info('detached', Object.keys(updates).length, 'old conversations for', oldPlate);
    }
  } catch (err) {
    log.warn('detachOldConversations failed:', /** @type {Error} */ (err).message);
  }
}

// Currently unused but kept so we can reach getDeviceId without an extra import
// when we move ownership-history audit logging here in a follow-up.
void getDeviceId;
